import React, { useEffect } from 'react';
import { Box, Flex, Text, Badge, HStack } from '@chakra-ui/react';
import { TtScore, Competitor } from '../../types/types';
import { debugTtScores } from '../../utils/debug';

interface TtScoreSummaryProps {
  ttScores: TtScore[];
  firstCompetitor: Competitor;
  secondCompetitor: Competitor;
  compact?: boolean; // Whether to use compact display for header
}

/**
 * Displays a summary of a table tennis match in the format: Player A Sets/Points : Player B Sets/Points,
 * marking the player currently serving.
 */
export const TtScoreSummary: React.FC<TtScoreSummaryProps> = ({
  ttScores,
  firstCompetitor,
  secondCompetitor,
  compact = false
}) => {
  useEffect(() => {
    debugTtScores(ttScores, 'TtScoreSummary');
  }, [ttScores]);

  // If no scores are available, don't render anything
  if (!ttScores || ttScores.length === 0) {
    return null;
  }

  // Get the latest score (highest sequence ID)
  const latestScore = ttScores.reduce((latest, current) =>
    current.sequenceId > latest.sequenceId ? current : latest,
    ttScores[0]
  );

  // Players are keyed by DB competitor id, matching firstCompetitor/secondCompetitor
  const firstScore = latestScore.players?.find(p => p.competitorId === firstCompetitor.id);
  const secondScore = latestScore.players?.find(p => p.competitorId === secondCompetitor.id);

  if (!firstScore || !secondScore) {
    console.error('Missing player scores in table tennis data', { firstScore, secondScore });
    return null;
  }

  const firstServing = latestScore.serverCompetitorId === firstCompetitor.id;
  const secondServing = latestScore.serverCompetitorId === secondCompetitor.id;

  if (compact) {
    // Compact version for header
    return (
      <HStack spacing={2} ml={2}>
        <Badge colorScheme="blue" fontSize="xs" px={1}>
          {firstCompetitor.name.substring(0, 3).toUpperCase()}
        </Badge>
        <Text fontSize="sm" fontWeight={firstServing ? "bold" : "normal"}>
          {firstScore.setsWon}/{firstScore.currentSetPoints}
          {firstServing && <Text as="span" ml={1} title="Serving">🏓</Text>}
        </Text>
        <Text fontSize="xs" color="gray.500">vs</Text>
        <Text fontSize="sm" fontWeight={secondServing ? "bold" : "normal"}>
          {secondScore.setsWon}/{secondScore.currentSetPoints}
          {secondServing && <Text as="span" ml={1} title="Serving">🏓</Text>}
        </Text>
        <Badge colorScheme="green" fontSize="xs" px={1}>
          {secondCompetitor.name.substring(0, 3).toUpperCase()}
        </Badge>
      </HStack>
    );
  }

  // Standard version
  return (
    <Box mt={2}>
      <Flex direction="row" justify="space-between" align="center">
        {/* First Player Score */}
        <Flex direction="column" align="center" flex={1}>
          <Text fontWeight="bold" fontSize="sm">{firstCompetitor.name}</Text>
          <Flex align="center">
            <Text fontSize="xl" fontWeight="bold">{firstScore.setsWon}</Text>
            <Text fontSize="md" ml={1}>({firstScore.currentSetPoints})</Text>
            {firstServing && <Badge colorScheme="green" ml={2}>Serve</Badge>}
          </Flex>
        </Flex>

        {/* Separator */}
        <Text mx={4} fontSize="xl" color="gray.400">vs</Text>

        {/* Second Player Score */}
        <Flex direction="column" align="center" flex={1}>
          <Text fontWeight="bold" fontSize="sm">{secondCompetitor.name}</Text>
          <Flex align="center">
            <Text fontSize="xl" fontWeight="bold">{secondScore.setsWon}</Text>
            <Text fontSize="md" ml={1}>({secondScore.currentSetPoints})</Text>
            {secondServing && <Badge colorScheme="green" ml={2}>Serve</Badge>}
          </Flex>
        </Flex>
      </Flex>
      <Text textAlign="center" fontSize="xs" color="gray.500" mt={1}>
        Set {latestScore.currentSet} — sets won (current-set points)
      </Text>
    </Box>
  );
};

export default TtScoreSummary;
